<template>
	<template v-if="visible">
		<input v-if="modeHidden" type="text" v-model="formattedValue" :class="[clazz, errorClass, fmtClass]"
			   :style="style" v-bind="tabindexBinding()" :readonly="ro" @focus="edit=true" @blur="edit=false"/>
		<div v-else class="inputs-row fd-money">
			<input type="text" v-model="formattedValue" :class="[clazz, errorClass, fmtClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro" @focus="edit=true" @blur="edit=false"/>
			<select v-if="editableCurrency" v-model="currencyValue" v-bind="tabindexBinding(1)">
				<option v-for="c in currencies" :value="c.code">{{c.code}}</option>
			</select>
			<span v-else class="fd-static-currency">{{ currencyValue }}</span>
		</div>
	</template>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "./Editor";
import {computed, ref} from "vue";
import {getter, setter} from "../Part";
import type {Money} from "@t/fd/editor/Money";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;

const m = computed<Money>(() => props.part as Money);
const {visible, value, rawValue, localData, formData, formPart, clazz, style, ro, errorClass, tabindexBinding} = useEditor(props);

const mode = computed(() => m.value.currencyMode || "hidden");
const modeHidden = computed(() => "hidden" === mode.value);
const editableCurrency = computed(() => !ro.value && mode.value === "edit");
const edit = ref<boolean>(false);

const ALLOWED = "0123456789,.-";
const DIGITS = "0123456789";
const formattedValue = computed<string>({
	get():string {
		if (edit.value) {
			return value.value;
		} else {
			const rw = rawValue.value;
			if (rw == null || Number.isNaN(rw)) return "";
			let dg = selectedCurrency.value.fractionDigits;
			if (Number.isInteger(rw)) dg = 0;	// this is int
			const nf = new Intl.NumberFormat("en-US", {
				style: "decimal",
				useGrouping: "always",
				trailingZeroDisplay: "stripIfInteger",
				roundingMode: "halfEven",
				minimumFractionDigits: dg,
				maximumFractionDigits: dg
			});
			const fmt = nf.format(rw);
			return fmt.replace(',', ' ');
		}
	},
	set(v:string):void {
		if (edit.value && v) {
			// remove unwanted chars
			let filtered = "";
			for (let i = 0; i < v.length; i++) {
				const ch = v.charAt(i);
				if (ALLOWED.indexOf(ch) >= 0) filtered += ch;
			}
			// what decimal point is used
			const commaIdx = filtered.indexOf(',');
			const dotIdx = filtered.indexOf('.');
			if (commaIdx > dotIdx) {
				filtered = filtered.replace('.', '').replace(',', '.');
			} else {
				filtered = filtered.replace(',', '');
			}
			console.log("num is "+filtered);
			value.value = filtered;
		} else value.value = v;	// direct
	}
});

const fmtClass = computed(() => {
	if (typeof rawValue.value === "number") {
		if (rawValue.value < 0) return "neg";
	}
	return null;
});

const currencyValue = computed({
	get() {
		if (m.value.currencyProperty) return getter(localData.value, m.value.currencyProperty, formData);
		else if (m.value.currency) return m.value.currency;
		return window.Server.defaultCurrency;
	},
	set(v: any) {
		if (m.value.currencyMode) setter(localData.value, m.value.currencyProperty, v, formData);
	}
});
const currencies = computed(() => {
	const cmap = window.Server.currencies;
	const clist = [];
	for (let citem of Object.keys(cmap)) {
		clist.push(cmap[citem]);
	}
	return clist;
});
const selectedCurrency = computed(() => {
	const c = currencyValue.value;
	for (let cr of currencies.value) {
		if (cr.code === c) return cr;
	}
	return null;
});
</script>

<style>
.fd-money {
	& > input[type=text] {
		text-align: right;
		max-width: 12ch;
	}

	& > select {
		border-left: 1px solid var(--border-color);
	}

	& .fd-static-currency {
		background-color: var(--input-bg);
		border-left: 1px solid var(--border-color);
		padding: var(--input-v-padding) var(--input-h-padding);
		font-size: var(--input-font-size);
		line-height: var(--input-height);
		color: var(--input-disabled-fg);
	}
}

.fd-negative {
	color: #880000;
}
</style>
